/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, TestAlertResponse2 } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

type TestAlertMutationConfig = {
  onSuccess?: (data: TestAlertResponse2) => void;
  onError?: (error: ApiError) => void;
};

export function useTestAlertMutation({ onSuccess, onError }: TestAlertMutationConfig = {}) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const queryClient = useQueryClient();

  const { mutate, isError, error, isPending } = useMutation<
    TestAlertResponse2,
    ApiError,
    { id: string; requestBody: { eventType: 'hallmark' | 'deflection' | 'signal' | 'lifecycle' } }
  >({
    mutationFn: ({ id, requestBody }) => {
      return mgmtPlaneAPIClient.alerts.testAlert({
        tenantId: selectedTenantId!,
        id,
        requestBody,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['alertResponses', selectedTenantId, variables.id] });
      onSuccess?.(data);
    },
    onError,
  });

  return { mutate, isError, error, isPending };
}
